import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Alert, Autocomplete, Button, CircularProgress, Divider, Icon, MenuItem, Select, Spacer, Stack, TextField, Tooltip, Typography, useTheme } from '@emburse/embark-core';
import { ArrowForwardIosIcon } from '@emburse/embark-icons';
import { isEqual, startCase } from 'lodash';
import { getNexusServiceAccounts, getNexusServiceVendors, saveNexusServiceConfiguration } from '../../../api/nexusService';
import { reloadUDFCache } from '../../../api/api';
import { getCompanyUDF, saveCompanyUDF } from '../../../api/companyUDF';
import { getPCardMasters } from '../../../api/pCardMaster';
import { ERPGatewayAvailableFeatures } from '../../../interfaces/ERPGateway';
import { EmployeeSearch } from '../../../components';
import { Employee, EmpType } from '../../../interfaces/Employee';
import { $t, useTranslation } from '../../../hooks/useTranslation';
import useLoadingOverlay from '../../../hooks/useLoadingOverlay';
const dimensionProerties = {
    NetSuite: '',
    Name: '',
    HasMapping: false,
    InUse: false,
    NetSuiteID: null,
    FluffyName: ''
};
const dimensions = [
    {
        id: 1,
        GLD: 'Employee GLD 1',
        Certify: 'EmpUDF1',
        ...dimensionProerties
    },
    {
        id: 2,
        GLD: 'Employee GLD 2',
        Certify: 'EmpUDF2',
        ...dimensionProerties
    },
    {
        id: 3,
        GLD: 'Employee GLD 3',
        Certify: 'EmpUDF3',
        ...dimensionProerties
    },
    {
        id: 4,
        GLD: 'Employee GLD 4',
        Certify: 'EmpUDF4',
        ...dimensionProerties
    },
    {
        id: 5,
        GLD: 'Employee GLD 5',
        Certify: 'EmpUDF5',
        ...dimensionProerties
    },
    {
        id: 6,
        GLD: 'Expense GLD 1',
        Certify: 'ExpRptUDF1',
        ...dimensionProerties
    },
    {
        id: 7,
        GLD: 'Expense GLD 2',
        Certify: 'ExpRptUDF2',
        ...dimensionProerties
    },
    {
        id: 8,
        GLD: 'Expense GLD 3',
        Certify: 'ExpRptUDF3',
        ...dimensionProerties
    },
    {
        id: 9,
        GLD: 'Expense GLD 4',
        Certify: 'ExpRptUDF4',
        ...dimensionProerties
    },
    {
        id: 10,
        GLD: 'Expense GLD 5',
        Certify: 'ExpRptUDF5',
        ...dimensionProerties
    }
];
const ERPGatewayMapping = forwardRef(({ catchError, isSyncPending, snackbarHandler, configuration, getConfiguration }, ref) => {
    useTranslation();
    const theme = useTheme();
    const { applyLoadingState, removeLoadingState } = useLoadingOverlay();
    const [loading, setLoading] = useState(false);
    const [cardPrograms, setCardPrograms] = useState([]);
    const [cardsLoading, setCardsLoading] = useState(false);
    const [netSuiteCards, setNetSuiteCards] = useState([]);
    const [netSuiteFields, setNetSuiteFields] = useState([]);
    const [GLDs, setGLDs] = useState(dimensions);
    const [companyUDF, setCompanyUDF] = useState(null);
    const [approver, setApprover] = useState((!!configuration && configuration.DefaultAccountant) || null);
    const useCorpCardAccount = !!configuration &&
        !!configuration.Features.find((item) => item.Feature === ERPGatewayAvailableFeatures.corporate_cards_account).InUse;
    const useCorpCardVendor = !!configuration &&
        !!configuration.Features.find((item) => item.Feature === ERPGatewayAvailableFeatures.corporate_cards_vendor).InUse;
    const fetchCardPrograms = useCallback(async () => {
        let addedNetSuite = [];
        try {
            const response = await getPCardMasters();
            const responseJSON = await response.json();
            addedNetSuite = responseJSON.map((item) => ({ ...item, NetSuite: '' }));
        }
        catch (err) {
            catchError(err);
        }
        return addedNetSuite;
    }, [catchError]);
    const fetchCardAccounts = useCallback(async () => {
        let responseJSON = [];
        try {
            const response = await getNexusServiceAccounts();
            responseJSON = await response.json();
        }
        catch (err) {
            catchError(err);
        }
        return responseJSON;
    }, [catchError]);
    const fetchCardVendors = useCallback(async () => {
        let responseJSON = [];
        try {
            const response = await getNexusServiceVendors();
            responseJSON = await response.json();
        }
        catch (err) {
            catchError(err);
        }
        return responseJSON;
    }, [catchError]);
    const getAllCards = useCallback(async () => {
        let cards = [];
        if (useCorpCardAccount) {
            let cardAccounts = await fetchCardAccounts();
            cardAccounts = cardAccounts.map((item) => ({ ...item, target: 'account' }));
            cards = [...cards, ...cardAccounts];
        }
        if (useCorpCardVendor) {
            let cardVendors = await fetchCardVendors();
            cardVendors = cardVendors.map((item) => ({ ...item, target: 'vendor' }));
            cards = [...cards, ...cardVendors];
        }
        if (cards.length > 0) {
            const firstOptions = [
                { id: -1, name: 'None' },
                { id: -2, name: 'Export as Reimbursable Expenses' }
            ];
            cards.unshift(...firstOptions);
        }
        setNetSuiteCards(cards);
    }, [fetchCardAccounts, fetchCardVendors, useCorpCardAccount, useCorpCardVendor]);
    const mapCardProgramData = useCallback(async () => {
        const programs = await fetchCardPrograms();
        const mappedCardPrograms = programs.map((item) => {
            const updatedItem = { ...item };
            if (configuration?.Mappings?.CardPrograms) {
                configuration.Mappings.CardPrograms.map((cardProgram) => {
                    if (cardProgram.Certify === item.Name) {
                        updatedItem.NetSuite = cardProgram.NetSuite;
                    }
                    if (cardProgram.PCardMasterPK === item.Id) {
                        updatedItem.NetSuiteID = cardProgram.NetSuiteID;
                        updatedItem.Target = cardProgram?.Target;
                    }
                    return cardProgram;
                });
            }
            return updatedItem;
        });
        setCardPrograms(mappedCardPrograms);
    }, [fetchCardPrograms, configuration]);
    const configureFeatureData = useCallback(() => {
        // AvailableMappingFields might be all we need to know for what is in use
        // but will cross reference with what is in the Features object marked as InUse
        // let allowedFeatures = [];
        // if (!!configuration && configuration.AvailableMappingFields.length > 0) {
        //    allowedFeatures = configuration.AvailableMappingFields.map((feature) => feature.Name);
        // }
        // if (!!configuration && configuration.Features.length > 0) {
        //    const features = configuration.Features.filter((item) => item.InUse && allowedFeatures.includes(item.Feature.toLowerCase()));
        //    const mappedFeatures = ['None', ...features.map((item) => item.Feature)];
        //    setNetSuiteFields(mappedFeatures);
        // }
        if (!!configuration &&
            !!configuration.AvailableMappingFields &&
            configuration.AvailableMappingFields.length > 0) {
            const mappedFeatures = [
                { Name: 'None' },
                ...configuration.AvailableMappingFields.map((item) => item)
            ];
            setNetSuiteFields(mappedFeatures);
        }
    }, [configuration]);
    const fetchCompanyUDF = useCallback(async () => {
        let responseJSON = null;
        try {
            const response = await getCompanyUDF();
            responseJSON = await response.json();
        }
        catch (err) {
            catchError(err);
        }
        return responseJSON;
    }, [catchError]);
    const mapGLDData = useCallback(async () => {
        const UDF = await fetchCompanyUDF();
        setCompanyUDF(UDF);
        const mappedGLDs = GLDs.map((item) => {
            const updatedItem = { ...item };
            if (configuration?.Mappings?.GLDs) {
                configuration.Mappings.GLDs.map((GLD) => {
                    if (GLD.Certify === item.Certify) {
                        const fluffyName = configuration.AvailableMappingFields.find(({ Name }) => Name === GLD.NetSuite)?.FluffyName || null;
                        updatedItem.NetSuite = GLD.NetSuite;
                        updatedItem.HasMapping = true;
                        updatedItem.NetSuiteID = GLD.NetSuiteID;
                        updatedItem.FluffyName = fluffyName;
                    }
                    return GLD;
                });
            }
            updatedItem.InUse = UDF[`Use${item.Certify}`];
            updatedItem.Name = updatedItem.NetSuite || UDF[`${item.Certify}Label`] || '';
            return updatedItem;
        });
        setGLDs(mappedGLDs);
    }, [GLDs, configuration, fetchCompanyUDF]);
    const fetchNetSuiteCompanyConfigAndMap = useCallback(async () => {
        try {
            if (configuration) {
                configureFeatureData();
                await mapCardProgramData();
                await mapGLDData();
            }
        }
        catch (err) {
            catchError(err);
        }
    }, [catchError, configuration, configureFeatureData, mapCardProgramData, mapGLDData]);
    const getCardsAndMap = useCallback(() => {
        setLoading(true);
        setCardsLoading(true);
        getAllCards();
        fetchNetSuiteCompanyConfigAndMap().finally(() => {
            setLoading(false);
            setCardsLoading(false);
        });
    }, [getAllCards, fetchNetSuiteCompanyConfigAndMap]);
    useEffect(() => {
        getCardsAndMap();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useImperativeHandle(ref, () => ({
        getCardsAndMap() {
            getCardsAndMap();
        }
    }));
    const setNetSuiteAccount = (id, value) => {
        const name = value?.target === 'vendor' ? value.company_name || value.vendor_id : value.name;
        let cardProgramsCopy = [...cardPrograms];
        cardProgramsCopy = cardProgramsCopy.map((item) => {
            const updatedItem = { ...item };
            if (item.Id === id) {
                updatedItem.NetSuite = name;
                updatedItem.Target = value?.target;
                updatedItem.NetSuiteID =
                    name === 'Export as Reimbursable Expenses' ? value.id : value?.internal_id;
            }
            return updatedItem;
        });
        setCardPrograms(cardProgramsCopy);
    };
    const getFluffyName = (name) => {
        const fluffyName = configuration?.AvailableMappingFields.find((item) => item.Name === name)?.FluffyName;
        return fluffyName || name;
    };
    const setNetSuiteGLDs = (e, id, nameOnly = false) => {
        let GLDsCopy = [...GLDs];
        GLDsCopy = GLDsCopy.map((item) => {
            const updatedItem = { ...item };
            if (item.id === id) {
                if (nameOnly) {
                    updatedItem.Name = e.target.value;
                    updatedItem.FluffyName = getFluffyName(e.target.value);
                    return updatedItem;
                }
                updatedItem.NetSuite = e.target.value;
                updatedItem.FluffyName = getFluffyName(e.target.value);
                updatedItem.NetSuiteID =
                    configuration.AvailableMappingFields.find(({ Name }) => Name === e.target.value).CustomRecordId || null;
                updatedItem.Name =
                    e.target.value === 'None' ||
                        e.target.value === 'Export as Reimbursable Expenses'
                        ? ''
                        : e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1);
            }
            return updatedItem;
        });
        setGLDs(GLDsCopy);
    };
    const newCardProgramConfig = () => {
        const hasCardPrograms = cardPrograms.filter((item) => (!!item.NetSuite && item.NetSuite !== 'None') ||
            (!!item.NetSuiteID && item.NetSuiteID !== -1));
        return hasCardPrograms.map((item) => ({
            NetSuite: item.NetSuite,
            Certify: item.Name,
            Target: item.Target,
            PCardMasterPK: item.Id,
            NetSuiteID: item.NetSuiteID
        }));
    };
    const newGLDConfig = () => {
        const hasGLDs = GLDs.filter((item) => !!item.NetSuite && item.NetSuite !== 'None');
        return hasGLDs.map((item) => ({
            NetSuite: item.NetSuite,
            Certify: item.Certify,
            ...(item.NetSuiteID && { NetSuiteID: item.NetSuiteID })
        }));
    };
    const updatedConfiguration = () => {
        if (!configuration) {
            return null;
        }
        const updatedConfig = { ...configuration };
        if (approver) {
            updatedConfig.DefaultAccountant = {
                EmpID: approver.EmpID || approver.PK,
                FName: approver.FName || approver.FirstName,
                LName: approver.LName || approver.LastName,
                Email: approver.Email
            };
        }
        else {
            updatedConfig.DefaultAccountant = null;
        }
        updatedConfig.Mappings = {
            GLDs: newGLDConfig(),
            CardPrograms: newCardProgramConfig()
        };
        return updatedConfig;
    };
    const newUDFData = () => {
        const updatedUDF = JSON.parse(JSON.stringify(companyUDF));
        GLDs.map((item) => {
            updatedUDF[`${item.Certify}Label`] = item.FluffyName || null;
            // Set in use if they mapped a GLD
            updatedUDF[`Use${item.Certify}`] = !!item.NetSuite && item.NetSuite !== 'None';
            return item;
        });
        return updatedUDF;
    };
    const duplicateArray = () => {
        const mappedGLDs = GLDs.map((item) => item.NetSuite).filter((item) => !!item);
        const duplicates = mappedGLDs.filter((item, index) => mappedGLDs.indexOf(item) !== index && item !== 'None');
        return duplicates;
    };
    const saveMapping = async () => {
        if (duplicateArray().length > 0) {
            const message = `${duplicateArray().join(', ')} ${$t('has been mapped more than once. Please update your mapping and Save.')}`;
            snackbarHandler(message, 'error');
            return;
        }
        const updatedConfig = updatedConfiguration();
        applyLoadingState();
        try {
            await saveNexusServiceConfiguration(updatedConfig);
            await saveCompanyUDF(newUDFData());
            await getAllCards();
            await reloadUDFCache();
            await getConfiguration();
            removeLoadingState();
            snackbarHandler($t('Mapping saved successfully'), 'success');
        }
        catch (err) {
            removeLoadingState();
            catchError(err);
        }
    };
    const optionLabel = (option) => {
        if (option.FName) {
            return `${option.FName} ${option.LName} (${option.Email})`;
        }
        if (option.Email) {
            return `${option.FullName} (${option.Email})`;
        }
        return '';
    };
    const cardOptionLabel = (option) => {
        if (option?.target) {
            if (option?.target === 'account') {
                return `${$t('Account')} - ${option.name}`;
            }
            if (option?.target === 'vendor') {
                if (option.copmany_name) {
                    return `${$t('Vendor')} - ${option.company_name}`;
                }
                return `${$t('Vendor')} - ${option.vendor_id}`;
            }
        }
        if (option?.id === -1 || option?.id === -2) {
            return option.name;
        }
        if (option) {
            const program = netSuiteCards.find((item) => item.name === option ||
                item.company_name === option ||
                item.vendor_id === option);
            if (program?.target) {
                const target = program.target.charAt(0).toUpperCase() + program.target.slice(1);
                return `${target} - ${program.name || program.company_name || program.vendor_id}`;
            }
            return option;
        }
        return '';
    };
    const allowedGLDFeatures = (gld) => {
        const menuItem = (Name, inUse, isSupportedType, hoverText, CustomRecordId, FluffyName) => {
            const itemContent = (_jsxs(_Fragment, { children: [isSupportedType && CustomRecordId && Name, isSupportedType && !CustomRecordId && startCase(FluffyName), !isSupportedType && _jsx(Typography, { children: startCase(FluffyName) })] }));
            return isSupportedType ? (_jsx(MenuItem, { value: Name, disabled: inUse || !isSupportedType, children: itemContent }, Name)) : (_jsx(Tooltip, { title: hoverText, placement: "top", children: _jsx(MenuItem, { value: Name, disabled: inUse || !isSupportedType, children: itemContent }) }, Name));
        };
        return netSuiteFields.map(({ Name, Target, CustomRecordId, FieldType, FluffyName }) => {
            const inUse = GLDs.find((GLD) => GLD.NetSuite === Name && Name !== 'None') || false;
            const isSupportedType = FieldType?.toLowerCase() === 'list' || FieldType?.toLowerCase() === 'select';
            const hoverText = $t("This type of custom field is not currently supported for import. Only 'List/Record' custom field types are supported at this time.");
            if (gld.GLD.includes('Employee')) {
                if (Target?.toLowerCase() === 'all' ||
                    Target?.toLowerCase() === 'employee' ||
                    Target?.toLowerCase() === 'vendor') {
                    return menuItem(Name, inUse, isSupportedType, hoverText, CustomRecordId, FluffyName);
                }
                return null;
            }
            if (gld.GLD.includes('Expense')) {
                if (Target?.toLowerCase() === 'all' || Target?.toLowerCase() === 'expense') {
                    return menuItem(Name, inUse, isSupportedType, hoverText, CustomRecordId, FluffyName);
                }
                return null;
            }
            return menuItem(Name, inUse, isSupportedType, hoverText, CustomRecordId, FluffyName);
        });
    };
    const createCardPrograms = () => cardPrograms.map((item) => (_jsxs("div", { children: [_jsx(Spacer, { size: 10 }), _jsxs(Stack, { spacing: 2, direction: "row", justifyContent: "space-between", children: [_jsxs("div", { style: {
                            width: '100%',
                            maxWidth: '500px',
                            display: 'flex',
                            justifyContent: 'space-between'
                        }, children: [_jsx("span", { style: {
                                    backgroundColor: theme.palette.action.hover,
                                    height: '40px',
                                    width: '100%'
                                }, children: _jsx(Typography, { style: { padding: '10px' }, children: item.Name }) }), _jsx(Icon, { iconUrl: ArrowForwardIosIcon, style: {
                                    fontSize: '1rem',
                                    color: theme.palette.monochrome.main,
                                    alignSelf: 'center',
                                    paddingLeft: '5px'
                                } })] }), _jsx(Autocomplete, { id: item.Id, style: { width: '100%', minWidth: 'auto' }, label: $t('Map Account'), value: item?.NetSuite, options: netSuiteCards, disableClearable: true, disabled: isSyncPending, onChange: (_e, value) => setNetSuiteAccount(item.Id, value), getOptionLabel: cardOptionLabel, isOptionEqualToValue: (option, value) => option.name === value ||
                            option.company_name === value ||
                            option.vendor_id === value })] })] }, item.Id)));
    const createGLDs = () => GLDs.map((item) => (_jsxs("div", { children: [_jsx(Spacer, { size: 10 }), _jsxs(Stack, { spacing: 2, direction: "row", justifyContent: "space-between", children: [_jsx("div", { style: {
                            width: '100%',
                            maxWidth: '250px',
                            display: 'flex',
                            justifyContent: 'space-between'
                        }, children: _jsx(Typography, { variant: "body2", style: { padding: '10px' }, children: item.GLD }) }), !item.HasMapping && !item.InUse && (_jsx(Select, { id: String(item.id), style: { width: '100%', minWidth: 'auto' }, label: $t('Map Account'), value: item?.NetSuite, defaultValue: item?.NetSuite, disabled: isSyncPending, onChange: (e) => setNetSuiteGLDs(e, item.id), children: allowedGLDFeatures(item) })), item.HasMapping && (_jsx(Tooltip, { title: $t('Expenses have been submitted and linked to values in this field. Changes to mapping can no longer be made.'), placement: "top", children: _jsx(Select, { id: String(item.id), style: { width: '100%', minWidth: 'auto' }, label: $t('Map Account'), value: item?.NetSuite, defaultValue: item?.NetSuite, readOnly: true, children: allowedGLDFeatures(item) }) })), item.InUse && !item.HasMapping && (_jsx(Tooltip, { title: $t('Expenses have been submitted and linked to values in this field. Changes to mapping can no longer be made.'), placement: "top", children: _jsx(TextField, { name: "Map Account", label: "", style: { width: '100%' }, defaultValue: $t('In Use'), value: $t('In Use'), readOnly: true }) })), _jsx(TextField, { name: "Name", style: { width: '100%' }, defaultValue: item.FluffyName || getFluffyName(item.Name), value: item.FluffyName || getFluffyName(item.Name), disabled: (item.HasMapping && !item.InUse) || item.InUse || isSyncPending, onChange: (e) => setNetSuiteGLDs(e, item.id, true) })] }), _jsx(Divider, { orientation: "horizontal", variant: "fullWidth", style: { width: '100%', marginTop: '5px' }, light: true })] }, item.id)));
    const gldsView = () => {
        if (loading) {
            return (_jsx("div", { style: { margin: '20px', textAlign: 'center' }, children: _jsx(CircularProgress, {}) }));
        }
        return createGLDs();
    };
    const cardProgramsView = () => {
        if (cardsLoading) {
            return (_jsx("div", { style: { margin: '20px', textAlign: 'center' }, children: _jsx(CircularProgress, {}) }));
        }
        if (!cardsLoading && !useCorpCardAccount && !useCorpCardVendor) {
            return (_jsx(Typography, { align: "center", style: { padding: '30px 0' }, children: $t(`Credit card export disabled. To map your Card programs to an Account or Vendor,
                        open the settings page and toggle the options on the Expense tab.`) }));
        }
        if (!cardsLoading && cardPrograms.length > 0) {
            return createCardPrograms();
        }
        return (_jsx(Typography, { align: "center", style: { padding: '30px 0' }, children: $t('No active card programs found in Emburse Professional') }));
    };
    return (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "h5", children: $t('Users') }), _jsx(Spacer, { size: 20 }), _jsx(Alert, { severity: "info", children: $t(`Select a default Accountant User. The Accountant user will be set as the Accountant for each user created. 
                        If you wish to assign a unique Accountant for each user, leave the field below blank and assign the 
                        Accountant manually to each user's profile in Emburse.`) }), _jsx(Spacer, { size: 20 }), _jsxs(Stack, { spacing: 2, direction: "row", justifyContent: "flex-start", children: [_jsx("div", { style: {
                            width: '100%',
                            maxWidth: '200px',
                            display: 'flex',
                            justifyContent: 'space-between'
                        }, children: _jsx(Typography, { children: $t('Default Accountant') }) }), _jsx(EmployeeSearch, { id: "Default Accountant", label: $t('Select Accountant'), style: { width: '100%', maxWidth: '500px' }, value: approver?.PK || approver?.EmpID ? approver : null, disabled: isSyncPending, empTypes: [EmpType.Accountant], getOptionLabel: optionLabel, onSelect: (employee, reason) => {
                            if (reason === 'clear') {
                                setApprover(null);
                            }
                            else {
                                setApprover(new Employee(employee));
                            }
                        } })] }), _jsx(Spacer, { size: 50 }), _jsx(Typography, { variant: "h5", children: $t('Card Program') }), _jsx(Spacer, { size: 20 }), _jsx(Alert, { severity: "info", children: $t('Map your credit card program to the applicable corporate card account or vendor.') }), _jsx(Spacer, { size: 20 }), !cardsLoading &&
                cardPrograms.length > 0 &&
                (useCorpCardAccount || useCorpCardVendor) && (_jsxs(Stack, { spacing: 2, direction: "row", justifyContent: "flex-start", children: [_jsx("div", { style: {
                            width: '100%',
                            maxWidth: '500px',
                            display: 'flex',
                            justifyContent: 'space-between'
                        }, children: _jsx(Typography, { variant: "caption", children: $t('Card Program') }) }), _jsx(Typography, { variant: "caption", children: $t('Corporate Card Account') })] })), _jsx(Spacer, { size: 5 }), cardProgramsView(), _jsx(Spacer, { size: 50 }), _jsx(Typography, { variant: "h5", children: $t('Field Mapping') }), _jsx(Spacer, { size: 20 }), _jsx(Alert, { severity: "info", children: $t(`Map the applicable fields to an Employee or Expense General Ledger Dimension (GLD). 
                        Fields mapped to an Expense Report GLD will allow report submitters to change the field values on an expense by expense basis.`) }), _jsx(Spacer, { size: 20 }), _jsxs(Stack, { spacing: 2, direction: "row", justifyContent: "flex-start", children: [_jsx(Typography, { style: {
                            width: '100%',
                            maxWidth: '250px'
                        }, variant: "caption", children: $t('General Ledger Dimensions') }), _jsx(Typography, { style: {
                            width: '100%',
                            maxWidth: '490px'
                        }, variant: "caption", children: $t('Field') }), _jsx(Typography, { style: { width: '100%' }, variant: "caption", children: $t('Name') })] }), _jsx(Divider, { orientation: "horizontal", variant: "fullWidth", style: { width: '100%', marginTop: '15px' }, light: true }), gldsView(), _jsx(Spacer, { size: 30 }), _jsx(Button, { onClick: saveMapping, disabled: isSyncPending || isEqual(configuration, updatedConfiguration()), children: $t('Save Mapping') })] }));
});
export default ERPGatewayMapping;
